import React, { useEffect, useState } from 'react';
import DataTable from '../../../ui/DataTable/DataTable';
import { CellText, CellPlate, CellMoney } from '../../../ui/DataTable/cells';
import Button from '../../../ui/Button/Button';
import ActionMenu from '../../../ui/ActionMenu';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { Input, Select as KitSelect } from '../../../ui/Field/Field';
import { toast, confirmDialog } from '../../../ui/feedback';
import RequirePermission from '../../../components/RequirePermission';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';
import { finesService, fmtAED, errorMessage } from '../../../services/finesService';
import { Field, Pager, Pill, StatusPill, TabPill, ImagePill, PaidByControl, formatPlate } from './ui';
import FineDetailsModal from './FineDetailsModal';
import EjarRegisterModal from './EjarRegisterModal';
import ServiceChargeRemoveModal from './ServiceChargeRemoveModal';
import DoubledParkingRemoveModal from './DoubledParkingRemoveModal';
import RtaPaymentsModal from './RtaPaymentsModal';
import RtaReadNowModal from './RtaReadNowModal';
import PaidFinesExportModal from './PaidFinesExportModal';
import ReceiptPageModal from './ReceiptPageModal';
// the Proof cell lives beside the receipt words it uses (rtaPayments.js), tested there
import { ProofCell, showPaidBy, NOT_CHARGED_LABEL } from './rtaPayments';
import { templateCount } from './templateCount';
import TicketCopy from './TicketCopy';
import ProgressBar from '../../../ui/Progress/ProgressBar';
import { useLiveCustomerNames, customerOf } from './liveNames';
import ContactPhone from '../../../components/ContactPhone';

const SIZE = 200;          // a long list: 200 fines a page (the engine allows up to 500)
const EMPTY = { q: '', branch: '', status: '', match: '', source: '', has_image: '', impound: '', tab: '', date_from: '', date_to: '', first_seen: '', paid_on: '', paid_by: '', review: '', ejar: '' };

// What the nightly Ejar check found for a fine (fines_history.ejar_status)
const EjarPill = ({ fine, size }) => {
    const s = fine.ejar_status;
    if (!s) return null;
    const note = fine.ejar_note || '';
    // a staff mistype of the ticket number on Ejar (owner, 6 Oct 2026): the right number AND a mistyped twin on the
    // contract = the customer charged twice; the fine's line under a wrong number = fix the number
    if (note.includes('charged twice on Ejar')) return <Pill tone="red" size={size} title={`${note} · checked ${fine.ejar_checked_at || ''}`}>Charged twice on Ejar</Pill>;
    if (s === 'registered' && note.includes('wrong number on Ejar')) return <Pill tone="amber" size={size} title={`${note} · checked ${fine.ejar_checked_at || ''}`}>Wrong number on Ejar</Pill>;
    if (s === 'registered' && note) return <Pill tone="amber" size={size} title={`Right amount on Ejar, but: ${note} · checked ${fine.ejar_checked_at || ''}`}>On Ejar · {note}</Pill>;
    if (s === 'registered') return <Pill tone="green" size={size} title={`On Ejar as ${fine.ejar_traffic_no || fine.ticket_number} · ${fine.ejar_checked_at || ''}`}>On Ejar</Pill>;
    if (s === 'amount-mismatch') return <Pill tone="amber" size={size} title={`Ejar has ${fmtAED(fine.ejar_amount)} — should be ${fmtAED(fine.billed_amount ?? fine.amount)}${note ? ` · ${note}` : ''}`}>Ejar {fmtAED(fine.ejar_amount)}{note ? ` · ${note.split(' (')[0]}` : ''}</Pill>;
    // its number is on the contract on Ejar but INACTIVE = the customer paid it (owner, 6 Oct 2026: staff delete a fine
    // the customer paid himself and Ejar keeps it inactive) - marked paid by the customer, never billed or registered
    if (s === 'missing' && note.includes('inactive on Ejar')) return <Pill tone="amber" size={size} title={`${note} · checked ${fine.ejar_checked_at || ''}`}>Customer paid · inactive on Ejar</Pill>;
    if (s === 'missing') return <Pill tone="red" size={size} title={`Not on the Ejar contract (checked ${fine.ejar_checked_at || ''})`}>Not on Ejar</Pill>;
    return <Pill tone="gray" size={size} title="contract not found on Ejar">Ejar ?</Pill>;
};

const Select = ({ label, value, onChange, options, children }) => (
    <Field label={label}>
        <KitSelect value={value} onChange={(e) => onChange(e.target.value)} placeholder="All" options={options}>
            {children}
        </KitSelect>
    </Field>
);

const REVIEW_LABEL = { inactive_plate_unpaid: 'Plate inactive', overlapping_windows: 'Overlapping contracts' };
const NO_RECEIPT = { paid: [], impounds: [] };   // a fine missing from the lookup's answer: no receipt, not `covered`
const CHIP_TEXT = { paid_by: { conflict: NOT_CHARGED_LABEL } };   // an active-filter chip whose value is a code

export default function FinesTab({ initialFilters }) {
    const [filters, setFilters] = useState({ ...EMPTY, ...(initialFilters || {}) });
    const [draftQ, setDraftQ] = useState(initialFilters?.q || '');
    const [page, setPage] = useState(1);
    const [facets, setFacets] = useState({ branches: [], sources: [], statuses: [], tabs: [] });
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [nonce, setNonce] = useState(0);   // bump to force a re-fetch even when the filters are unchanged
    const [checking, setChecking] = useState(null);   // "Check Ejar now" job in flight: {stage, done, total} (null = idle)
    const [registering, setRegistering] = useState(false);   // the Register on Ejar dialog
    const [exportingPaid, setExportingPaid] = useState(false);   // the Export paid fines dialog
    const [removingSvc, setRemovingSvc] = useState(false);   // the admin's: service charges not owed, off Ejar
    const [removingDbl, setRemovingDbl] = useState(false);   // the admin's: parking the engine charged twice, off Ejar
    const [paying, setPaying] = useState(false);             // Payments: RTA receipt PDFs read, then saved
    const [readingRta, setReadingRta] = useState(false);     // the company RTA account's payments read now
    const [payments, setPayments] = useState(null);          // {ticket: {paid, impounds}} of the page on screen
    const [paymentsNonce, setPaymentsNonce] = useState(0);   // bump after new receipts: the page's lookup again
    const [receipt, setReceipt] = useState(null);            // the proof window: {ref, ticket, at, p} of a Proof click
    const liveNames = useLiveCustomerNames(data?.items); // names straight from the yelo database
    const { hasPermission, isAdmin } = usePermissions();

    useEffect(() => { finesService.facets().then(setFacets).catch(() => {}); }, []);
    // the receipts of the fines on screen: ONE lookup a page (200 fines, the engine takes 300), never one a row. Keyed
    // on the tickets, so a "paid by" click (same tickets) does not read again. A failed lookup shows no payment pill.
    const pageTickets = (data?.items || []).map((f) => String(f.ticket_number)).join(',');
    useEffect(() => {
        setPayments(null);
        if (!pageTickets) return undefined;
        let stale = false;
        finesService.rtaPaymentsLookup(pageTickets.split(','))
            .then((r) => { if (!stale) setPayments(r || {}); })
            .catch(() => { if (!stale) setPayments(null); });
        return () => { stale = true; };
    }, [pageTickets, paymentsNonce]);
    // Reset only when the incoming filter CONTENT changes (not on every parent re-render),
    // so a search typed here is never wiped by an unrelated re-render.
    const initialKey = JSON.stringify(initialFilters || {});
    useEffect(() => { setFilters({ ...EMPTY, ...(initialFilters || {}) }); setDraftQ(initialFilters?.q || ''); setPage(1); }, [initialKey]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        setError(null);
        let stale = false;       // a slower earlier answer never lands under newer filters (review 18 Oct 2026)
        finesService.search({ ...filters, page, size: SIZE })
            .then((d) => { if (!stale) setData(d); })
            .catch((e) => { if (!stale) setError(errorMessage(e)); });
        return () => { stale = true; };
    }, [filters, page, nonce]);

    const set = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
    // Every Search click / Enter re-runs the query, even if the text is unchanged.
    const runSearch = () => { setFilters((f) => ({ ...f, q: draftQ })); setPage(1); setNonce((n) => n + 1); };
    const clearAll = () => { setFilters(EMPTY); setDraftQ(''); setPage(1); };
    const setPaidBy = async (ticket, who) => {
        try {
            const updated = await finesService.setPaidBy(ticket, who);
            setData((d) => d ? { ...d, items: d.items.map((f) => (f.ticket_number === ticket ? { ...f, ...updated } : f)) } : d);
        } catch (e) { setError(errorMessage(e)); }
    };
    // "Fines vs Ejar": fresh from Ejar first (contracts not checked within the hour), then the file
    const [reportStage, setReportStage] = useState('');
    const [moreOpen, setMoreOpen] = useState(false);     // the less used filters
    const downloadFinesVsEjar = async () => {
        if (!(await confirmDialog({
            title: 'Build the Fines vs Ejar report?',
            message: 'Built from the database (the nightly Ejar check keeps it current). Only contracts that got a new fine since their last Ejar check are read from Ejar first - usually none or a few seconds.',
            confirmLabel: 'Yes, build it',
        }))) return;
        setReportStage('looking for fines added since the last Ejar check…');
        try {
            const job = await finesService.finesVsEjar(
                { branch: filters.branch, date_from: filters.date_from, date_to: filters.date_to },
                (j) => setReportStage(j.stage || 'working…'));
            const n = job.refreshed?.contracts || 0;
            toast.success(n ? `Fines vs Ejar: ${n} contract(s) with new fines read from Ejar first - the file is up to date.`
                : 'Fines vs Ejar: no new fine since the last Ejar check - built straight from the database.');
            if (n) setNonce((x) => x + 1);
        } catch (e) { toast.error(errorMessage(e)); }
        setReportStage('');
    };
    const checkEjarNow = async () => {
        if (!(await confirmDialog({
            title: 'Check Ejar now?',
            message: 'Reads the Traffic Violations of up to 400 contracts from Ejar (~3 s each). Background runs pause meanwhile. It also runs by itself every night at 20:00.',
            confirmLabel: 'Yes, check Ejar',
        }))) return;
        setChecking({ stage: 'starting', done: 0, total: 0 });
        try {
            // a background job followed to its end (a ~20 min blocking request used to outlive the browser's wait)
            const r = await finesService.ejarCheckRun({ cap: 400 }, (j) => setChecking({ stage: j.stage, done: j.done || 0, total: j.total || 0 }));
            toast.success(`Ejar check: ${r.summary?.checked || 0} contract(s) · registered ${r.summary?.registered || 0} · wrong amount ${r.summary?.['amount-mismatch'] || 0} · missing ${r.summary?.missing || 0} · on Ejar only ${r.summary?.ejarOnly || 0} · duplicates ${r.summary?.duplicates || 0}
Download "Fines vs Ejar report" for the diagnosis of every difference.`);
            setNonce((n) => n + 1);
        } catch (e) { toast.error(errorMessage(e)); }
        setChecking(null);
    };
    // the company RTA account's receipts read now (the weekly read by hand): confirmed, then followed in its window
    const readRtaNow = async () => {
        if (!(await confirmDialog({
            title: 'Read the RTA account payments now?',
            message: 'The engine signs in to the company RTA account (its RTA login and the e-mail code), reads the transaction history since the last read and saves every receipt it does not hold yet. It also runs by itself every week. Nothing is paid or changed on RTA.',
            confirmLabel: 'Yes, read now',
        }))) return;
        setReadingRta(true);
    };
    const pages = data ? Math.max(1, Math.ceil(data.total / SIZE)) : 1;
    const active = Object.entries(filters).filter(([, v]) => v);

    const columns = [
        { key: 'n', header: '#', sortable: false, hideable: false, cell: (f, v, i) => (page - 1) * SIZE + i + 1 },
        { key: 'ticket_number', header: 'Ticket', sortable: false, hideable: false, cell: (f) => <TicketCopy ticket={f.ticket_number} /> },
        { key: 'plate_number', header: 'Plate', sortable: false, cell: (f) => <CellPlate>{formatPlate(f.plate_number)}</CellPlate> },
        { key: 'fine_date', header: 'Date', sortable: false, cell: (f) => <span className="ds-cell-date">{f.fine_date}</span> },
        {
            key: 'amount', header: 'Amount', sortable: false,
            cell: (f) => (
                <>
                    <div><CellMoney value={Number(f.billed_amount ?? f.amount ?? 0)} currency="AED" /></div>
                    {/* the company's staff: the RTA amount, no 5 % fee + VAT (owner, 2 Oct 2026) */}
                    {f.staff ? <div className="mt-1"><Pill tone="purple" size="sm" title="Staff on Ejar (Administration): the RTA amount, no 5 % fee + VAT">Staff · RTA amount</Pill></div> : null}
                    {(f.impound_value || f.impound_ejar_amount || (f.billed_service && f.paid_by !== 'customer') || (!f.billed_service && f.service_ejar_amount)) ? (
                        <div className="mt-1 flex flex-col items-center gap-1">
                            {/* the impound in ONE pill, like the service charge (owner, 6 Oct 2026: a red "30 days" beside a
                                "paid on Ejar" read as owed and paid at once - "paid" only meant charged on Ejar) */}
                            {f.impound_value || f.impound_ejar_amount ? <Pill tone={f.impound_ejar_amount ? 'gray' : 'red'} size="sm"
                                title={f.impound_ejar_amount ? `${f.impound_ejar_note || 'charged on Ejar'} · line ${f.impound_ejar_no || ''}` : 'not on Ejar yet'}>
                                Impound {fmtAED(f.impound_ejar_amount || f.billed_impound || f.impound_value)}{f.impound_days ? ` · ${f.impound_days} days` : ''}{f.impound_ejar_amount ? ' · on Ejar' : ' · not on Ejar yet'}</Pill> : null}
                            {/* a Non-Payable fine only: its own service charge, AED 350 + VAT (owner, 6 Oct 2026) */}
                            {f.billed_service && f.paid_by !== 'customer' ? <Pill tone={f.service_ejar_amount ? 'gray' : 'red'} size="sm"
                                title={f.service_ejar_amount ? `${f.service_ejar_note || 'on Ejar'} · line ${f.service_ejar_no || ''}` : 'AED 350 + VAT - not on Ejar yet'}>
                                Service charge {fmtAED(f.billed_service)} · black points{f.service_ejar_amount ? ' · on Ejar' : ''}</Pill> : null}
                            {/* a service charge line on Ejar that is not owed: only a Non-Payable fine carries one (owner, 6 Oct
                                2026) - said, never deleted by Yelo; a person removes it on Ejar */}
                            {!f.billed_service && f.service_ejar_amount ? <Pill tone="red" size="sm"
                                title={`${f.service_ejar_note || 'on Ejar'} · line ${f.service_ejar_no || ''} - only a Non-Payable fine owes a service charge`}>
                                Service charge {fmtAED(f.service_ejar_amount)} on Ejar · not due</Pill> : null}
                        </div>
                    ) : null}
                </>
            ),
        },
        {
            key: 'fines_source', header: 'Source', sortable: false, nowrap: false,
            // one line a row (more rows on screen): the violation text is on hover and in the fine window
            cell: (f) => <CellText title={f.offense ? `${f.fines_source} · ${f.offense}` : f.fines_source}>{f.fines_source}</CellText>,
        },
        { key: 'customer', header: 'Customer', sortable: false, nowrap: false, cell: (f) => <CellText>{customerOf(f, liveNames)}</CellText> },
        { key: 'phone', header: 'Phone', sortable: false, cell: (f) => <ContactPhone contractNo={f.contract_no} /> },
        { key: 'contract_no', header: 'Contract', sortable: false, type: 'id' },
        { key: 'branch', header: 'Branch', sortable: false, type: 'text' },
        {
            key: 'status', header: 'Status', sortable: false, nowrap: false,
            cell: (f) => (
                <div className="flex flex-wrap items-center gap-1 max-w-[280px]">
                    <StatusPill status={f.status} />
                    {showPaidBy(f, payments?.[String(f.ticket_number)]) && (
                        <RequirePermission permission="fines_set_paid_by"><PaidByControl fine={f} onChange={(who) => setPaidBy(f.ticket_number, who)} /></RequirePermission>
                    )}
                    <TabPill tab={f.portal_tab} size="sm" />
                    <EjarPill fine={f} size="sm" />
                    {f.review_reason && <Pill tone="amber" size="sm" title={f.review_reason}>{REVIEW_LABEL[f.review_reason] || 'Review'}</Pill>}
                </div>
            ),
        },
        // who really paid, with the proof (owner, 16 Oct 2026): RTA's receipt, one click opens it
        {
            key: 'proof', header: 'Proof', sortable: false, nowrap: false,
            cell: (f) => <ProofCell fine={f} pay={payments ? (payments[String(f.ticket_number)] || NO_RECEIPT) : undefined} onOpen={setReceipt} />,
        },
        { key: 'image', header: 'Image', sortable: false, cell: (f) => <ImagePill fine={f} /> },
    ];

    // One compact row in the table's own toolbar (next to Data & tools / Register / View): search,
    // Ejar status, dates. The rarely used filters sit behind "More filters", closed by default, so
    // the table keeps the screen; a filter that is on shows as a chip either way.
    const MORE_KEYS = ['branch', 'status', 'paid_by', 'match', 'source', 'has_image', 'impound', 'tab', 'review'];
    const moreActive = MORE_KEYS.filter((k) => filters[k]).length;
    const filterRow = (
        <div className="flex flex-wrap items-center gap-2 min-w-0">
            <div className="w-[190px] max-w-full">
                <Input type="search" aria-label="Search" value={draftQ} placeholder="Search ticket, plate, customer, contract"
                       onChange={(e) => setDraftQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && runSearch()} />
            </div>
            <div className="w-[165px]">
                <KitSelect aria-label="Ejar status" value={filters.ejar} onChange={(e) => set('ejar', e.target.value)} placeholder="Ejar: any">
                    <option value="not-on-ejar">Not on Ejar (missing or wrong amount)</option>
                    <option value="missing">Missing on Ejar</option>
                    <option value="mismatch">Wrong amount on Ejar</option>
                    <option value="registered">On Ejar</option>
                    <option value="unknown">Contract not on Ejar</option>
                    <option value="unchecked">Not checked yet</option>
                </KitSelect>
            </div>
            <Input type="date" aria-label="From" title="From" style={{ width: 132, flex: 'none' }} value={filters.date_from} onChange={(e) => set('date_from', e.target.value)} />
            <span className="text-gray-500 text-sm" aria-hidden="true">–</span>
            <Input type="date" aria-label="To" title="To" style={{ width: 132, flex: 'none' }} value={filters.date_to} onChange={(e) => set('date_to', e.target.value)} />
            <Button variant="secondary" leadingIcon={<Icon glyph={ICONS.search} size="sm" />} onClick={runSearch}>Search</Button>
            <Button variant={moreActive ? 'primary' : 'secondary'} aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}
                    trailingIcon={<Icon glyph={moreOpen ? ICONS.chevronUp : ICONS.chevronDown} size="sm" />}>
                Filters{moreActive ? ` (${moreActive})` : ''}
            </Button>
        </div>
    );
    const filterExtras = (moreOpen || active.length > 0) ? (
        <div className="w-full py-1">
            {moreOpen && (
                <div className="grid gap-3 items-end p-3 rounded-lg border border-gray-200 bg-gray-50 grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                    <Select label="Branch" value={filters.branch} onChange={(v) => set('branch', v)} options={facets.branches} />
                    <Select label="Status" value={filters.status} onChange={(v) => set('status', v)} options={facets.statuses} />
                    <Select label="Paid by" value={filters.paid_by} onChange={(v) => set('paid_by', v)}>
                        <option value="company">Company</option><option value="customer">Customer</option><option value="unset">Not set yet</option>
                        <option value="conflict">{NOT_CHARGED_LABEL}</option>
                    </Select>
                    <Select label="Matched" value={filters.match} onChange={(v) => set('match', v)}><option value="assigned">Assigned</option><option value="unassigned">Unassigned</option></Select>
                    <Select label="Source" value={filters.source} onChange={(v) => set('source', v)} options={facets.sources} />
                    <Select label="Image" value={filters.has_image} onChange={(v) => set('has_image', v)}><option value="without">Without image</option><option value="with">With image</option><option value="portal">From RTA</option><option value="offline">Offline card</option></Select>
                    <Select label="Impound" value={filters.impound} onChange={(v) => set('impound', v)}><option value="with">With impound</option><option value="without">Without</option></Select>
                    <Select label="RTA tab" value={filters.tab} onChange={(v) => set('tab', v)} options={facets.tabs || []} />
                    <Select label="Review" value={filters.review} onChange={(v) => set('review', v)}><option value="1">Needs review</option></Select>
                </div>
            )}
            {active.length > 0 && (
                <div className={`flex flex-wrap items-center gap-2 text-sm ${moreOpen ? 'mt-3' : ''}`}>
                    <span className="text-gray-500">Active filters:</span>
                    {active.map(([k, v]) => (
                        <Pill key={k} tone="indigo">
                            {k}: {CHIP_TEXT[k]?.[v] || v}
                            <button type="button" className="inline-flex items-center" onClick={() => set(k, '')} aria-label={`Remove filter ${k}`}>
                                <Icon glyph={ICONS.close} size="xs" />
                            </button>
                        </Pill>
                    ))}
                    <Button size="xs" variant="ghost" onClick={clearAll}>Clear all</Button>
                </div>
            )}
        </div>
    ) : null;

    const actions = (
        <>
            {/* Exports and the Ejar check grouped into one menu; Register on Ejar stays visible */}
            {reportStage && (
                <span role="status" className="inline-flex items-center gap-2 text-sm text-gray-700 border border-gray-200 bg-gray-50 rounded-lg px-3 py-1">
                    <span className="ds-spinner ds-spinner--sm" aria-hidden="true" />Fines vs Ejar: {reportStage}
                </span>
            )}
            {checking && (
                <div className="w-full sm:w-80 border border-gray-200 bg-gray-50 rounded-lg px-3 py-2">
                    <ProgressBar done={checking.done} total={checking.total} unit="contracts" state="running"
                        label="Checking Ejar" note={checking.stage} />
                </div>
            )}
            <ActionMenu
                label={checking ? 'Checking Ejar…' : 'Data & tools'}
                icon={ICONS.download}
                size="sm"
                width={320}
                items={[
                    { heading: 'Export' },
                    {
                        label: 'Export paid fines',
                        description: 'Excel of the fines paid at RTA between two payment dates or on chosen days, paid by anyone, the company or the customer: Summary (with the company gain on discounted fines), Details and the Ejar errors',
                        icon: ICONS.spreadsheet,
                        onClick: () => setExportingPaid(true),
                        hidden: !hasPermission(PERMISSIONS.export_traffic_fines),
                    },
                    {
                        label: 'Export company template',
                        description: 'Excel in the company upload layout for the fines matching the current filters (fines already on their Ejar contract, paid by the customer or with no contract are left out)',
                        icon: ICONS.spreadsheet,
                        onClick: () => finesService.downloadTemplate(filters, 'Traffic_Violations_filtered.xlsx')
                            .then((r) => { const t = templateCount(0, r); if (r.skipped || r.other) toast.info(`${t.value} ${t.note}.`); })
                            .catch((e) => toast.error(errorMessage(e))),
                        hidden: !hasPermission(PERMISSIONS.export_traffic_fines),
                    },
                    {
                        label: reportStage ? 'Preparing the report…' : 'Fines vs Ejar report',
                        description: 'Every difference between our fines and Ejar: not on Ejar, wrong amount, wrong customer, charged twice - with the money for / against us. Updates from Ejar first if older than an hour.',
                        icon: ICONS.download,
                        onClick: downloadFinesVsEjar,
                        disabled: !!reportStage,
                        hidden: !(hasPermission(PERMISSIONS.export_traffic_fines) && hasPermission(PERMISSIONS.run_traffic_fines)),
                    },
                    { divider: true },
                    {
                        label: checking ? 'Checking Ejar…' : 'Check Ejar now',
                        description: 'Read the Traffic Violations of every contract with fines from Ejar now (manual: background runs pause). Otherwise it runs every night at 20:00.',
                        icon: ICONS.refresh,
                        onClick: checkEjarNow,
                        disabled: !!checking,
                        // its own permission (phase 2, 4 Oct 2026) - today's rule: export AND run
                        hidden: !hasPermission('fines_check_ejar'),
                    },
                    {
                        label: 'Read RTA account payments now',
                        description: 'The company RTA account\'s payment receipts since the last read, saved to the fines (who paid, when, how) - the same read the engine runs every week. Nothing is paid on RTA.',
                        icon: ICONS.rta,
                        onClick: readRtaNow,
                        disabled: readingRta,
                        hidden: !hasPermission(PERMISSIONS.run_traffic_fines),
                    },
                    // the admin's alone, kept for emergencies (owner, 6 Oct 2026: "نخليه للطوارئ مع الادمن")
                    { divider: true, hidden: !isAdmin },
                    {
                        label: 'Remove service charges not owed',
                        description: 'Admin: the black points service charge lines on Ejar of fines outside the RTA Non-Payable tab - choose, then they are cancelled on Ejar under your own Ejar login',
                        icon: ICONS.close,
                        onClick: () => setRemovingSvc(true),
                        hidden: !isAdmin,
                    },
                    {
                        label: 'Remove parking charged twice',
                        description: 'Admin: the engine parking lines of one day on Ejar that double a staff line of the same car and money - choose, then they are removed on Ejar under your own Ejar login; the staff line stays',
                        icon: ICONS.close,
                        onClick: () => setRemovingDbl(true),
                        hidden: !isAdmin,
                    },
                ]}
            />
            {hasPermission(PERMISSIONS.run_traffic_fines) && (
                <Button size="sm" variant="secondary" leadingIcon={<Icon glyph={ICONS.wallet} size="sm" />} onClick={() => setPaying(true)}
                        title="RTA payment receipts (PDF): read and matched to the fines first, saved only when you press Save">
                    Payments
                </Button>
            )}
            <RequirePermission permission={PERMISSIONS.register_fines_on_ejar}>
                <Button size="sm" variant="primary" leadingIcon={<Icon glyph={ICONS.upload} size="sm" />} onClick={() => setRegistering(true)}
                        title="Register the fines missing on Ejar (current filters), one by one on each contract's screen - under your own Ejar login">
                    Register on Ejar
                </Button>
            </RequirePermission>
        </>
    );

    return (
        <div>
            {error && data && (
                <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
            )}
            <DataTable
                columns={columns}
                data={data?.items || []}
                rowKey="ticket_number"
                loading={!data && !error}
                error={!data ? error : null}
                searchable={false}
                pagination={false}
                toolbar={filterRow}
                filters={filterExtras}
                density="compact"
                actions={actions}
                onRowClick={(f) => setSelected(f.ticket_number)}
                emptyIcon={ICONS.fines}
                emptyTitle="No fines found"
                emptyDescription={active.length ? 'No fine matches the current filters.' : 'Nothing has been recorded yet.'}
                emptyAction={active.length ? <Button size="sm" onClick={clearAll}>Clear all filters</Button> : null}
                footer={data ? <Pager page={page} pages={pages} total={data.total} onPage={setPage} /> : null}
            />
            {/* new receipts change no fine (facts only): the page's lookup is read again, and the list with it */}
            {paying && <RtaPaymentsModal onClose={() => setPaying(false)} onDone={() => { setPaymentsNonce((n) => n + 1); setNonce((n) => n + 1); }} />}
            {readingRta && <RtaReadNowModal onClose={() => setReadingRta(false)} onDone={() => setPaymentsNonce((n) => n + 1)} />}
            {removingDbl && <DoubledParkingRemoveModal onClose={() => setRemovingDbl(false)} onDone={() => setNonce((n) => n + 1)} />}
            {removingSvc && <ServiceChargeRemoveModal onClose={() => setRemovingSvc(false)} onDone={() => setNonce((n) => n + 1)} />}
            {exportingPaid && <PaidFinesExportModal branches={facets.branches || []} onClose={() => setExportingPaid(false)} />}
            {registering && <EjarRegisterModal filters={filters} onClose={() => setRegistering(false)} onDone={() => setNonce((n) => n + 1)} />}
            {selected && <FineDetailsModal ticket={selected} onClose={() => setSelected(null)} />}
            {receipt && <ReceiptPageModal receipt={receipt} onClose={() => setReceipt(null)} />}
        </div>
    );
}
